import React, { useState, useEffect } from 'react';
import { Package, Send, AlertTriangle, Box } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminApi } from '@/utils/api';

export const GlobalBroadcastManager = () => {
    const [globalItemType, setGlobalItemType] = useState<'card' | 'skin' | 'package'>('card');
    const [assets, setAssets] = useState<{ cards: any[], skins: any[] }>({ cards: [], skins: [] });
    const [selectedGlobalItem, setSelectedGlobalItem] = useState<any | null>(null);
    const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        adminApi.getAssetsList()
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setAssets({ cards: data.cards || [], skins: data.skins || [] });
                }
            })
            .catch(console.error)
            .finally(() => setIsLoading(false));
    }, []);

    const showNotification = (message: string, type: 'success' | 'error') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const handleBroadcast = async () => {
        if (!selectedGlobalItem) return;
        
        setIsSubmitting(true);
        try {
            const res = await adminApi.giftAll({
                itemType: globalItemType,
                itemId: globalItemType === 'card' ? selectedGlobalItem.definition_id : globalItemType === 'package' ? selectedGlobalItem.name : selectedGlobalItem.skin_id
            });
            const data = await res.json();
            
            if (data.success) {
                showNotification(`Successfully broadcasted to ${data.usersAffected} users!`, 'success');
                setSelectedGlobalItem(null);
            } else {
                showNotification(data.error || 'Broadcast failed', 'error');
            }
        } catch (err) {
            showNotification('Network error during broadcast', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="w-full max-w-7xl mx-auto space-y-6">
            <div className="mb-8">
                <h2 className="text-2xl font-black text-white flex items-center gap-3">
                    <Package size={28} className="text-orange-500" />
                    Global Broadcast
                </h2>
                <p className="text-white/50 text-sm mt-2">
                    Send a Master Card or Profile Skin to EVERY registered user in the system simultaneously.
                </p>
            </div>

            <AnimatePresence>
                {notification && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className={`p-4 rounded-2xl border ${notification.type === 'success' ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-red-500/10 border-red-500/30 text-red-400'} font-bold flex items-center gap-3 mb-6`}
                    >
                        {notification.type === 'success' ? <Send size={20} /> : <AlertTriangle size={20} />}
                        {notification.message}
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Information Panel */}
                <div className="bg-[#111113] rounded-[32px] border border-orange-500/30 p-8 shadow-[0_0_50px_rgba(249,115,22,0.1)] flex flex-col items-center justify-center text-center">
                    <div className="w-24 h-24 rounded-full bg-orange-500/20 flex items-center justify-center mb-6 text-orange-500">
                        <Package size={48} />
                    </div>
                    <h3 className="text-2xl font-black text-white mb-4">Mass Distribution</h3>
                    <p className="text-white/60 leading-relaxed mb-6">
                        You are about to execute a global broadcast. This action will distribute the selected asset to <strong>ALL USERS</strong> in the database and send a system-wide bot notification.
                    </p>
                    <div className="px-6 py-3 rounded-full bg-red-500/10 text-red-500 text-xs font-black uppercase tracking-widest border border-red-500/20 flex items-center gap-2">
                        <AlertTriangle size={16} /> Irreversible Action
                    </div>
                </div>

                {/* Selection Panel */}
                <div className="bg-[#111113] rounded-[32px] border border-white/5 p-6 flex flex-col h-[600px]">
                    <div className="flex bg-[#1c1c1e] p-1.5 rounded-2xl mb-6 border border-white/5">
                        <button
                            onClick={() => { setGlobalItemType('card'); setSelectedGlobalItem(null); setSelectedPackage(null); }}
                            className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${globalItemType === 'card' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white'}`}
                        >
                            Master Cards
                        </button>
                        <button
                            onClick={() => { setGlobalItemType('package'); setSelectedGlobalItem(null); setSelectedPackage(null); }}
                            className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${globalItemType === 'package' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white'}`}
                        >
                            Packages
                        </button>
                        <button
                            onClick={() => { setGlobalItemType('skin'); setSelectedGlobalItem(null); setSelectedPackage(null); }}
                            className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${globalItemType === 'skin' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white'}`}
                        >
                            Profile Skins
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 mb-6">
                        {isLoading ? (
                            <div className="text-center py-10 text-white/40 font-bold animate-pulse">Loading Assets...</div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pb-2">
                                {globalItemType === 'card' ? (
                                    !selectedPackage ? (
                                        Array.from(new Set(assets.cards.map(c => c.package_name || 'Standard Editions'))).map(pkgName => {
                                            const pkgCards = assets.cards.filter(c => (c.package_name || 'Standard Editions') === pkgName);
                                            return (
                                                <div
                                                    key={pkgName as string}
                                                    onClick={() => setSelectedPackage(pkgName as string)}
                                                    className="bg-[#1c1c1e] p-4 rounded-2xl border border-white/5 hover:border-orange-500/30 cursor-pointer transition-all aspect-square flex flex-col items-center justify-center text-center group"
                                                >
                                                    <div className="w-12 h-12 rounded-xl bg-orange-500/10 text-orange-500 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                                        <Package size={24} />
                                                    </div>
                                                    <div className="font-black text-white text-xs truncate w-full px-2">{pkgName as string}</div>
                                                    <div className="text-[10px] text-white/40 mt-1">{pkgCards.length} Cards</div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <>
                                            <div className="col-span-full flex items-center justify-between mb-2">
                                                <span className="text-white font-black text-sm">{selectedPackage}</span>
                                                <button onClick={() => setSelectedPackage(null)} className="text-[10px] text-orange-500 uppercase tracking-widest font-bold hover:text-orange-400">Back to Packages</button>
                                            </div>
                                            {assets.cards.filter(c => (c.package_name || 'Standard Editions') === selectedPackage).map(card => {
                                                const isSelected = selectedGlobalItem?.definition_id === card.definition_id;
                                                return (
                                                    <div
                                                        key={card.definition_id}
                                                        onClick={() => setSelectedGlobalItem(card)}
                                                        className={`relative rounded-xl border overflow-hidden cursor-pointer transition-all aspect-[3/4] ${isSelected ? 'border-orange-500 shadow-[0_0_20px_rgba(249,115,22,0.3)] scale-[0.98]' : 'border-white/10 hover:border-white/30 hover:scale-[1.02]'}`}
                                                    >
                                                        {card.image_url ? (
                                                            <img src={card.image_url.startsWith('http') || card.image_url.startsWith('/') ? card.image_url : `/assets/cards/${card.image_url}`} alt={card.card_name} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <div className="w-full h-full bg-[#1c1c1e] flex flex-col items-center justify-center p-2 text-center">
                                                                <Box size={24} className="text-white/20 mb-2" />
                                                                <span className="text-[9px] text-white/40 uppercase font-black">{card.card_name}</span>
                                                            </div>
                                                        )}
                                                        {isSelected && (
                                                            <div className="absolute inset-0 bg-orange-500/20 flex items-center justify-center backdrop-blur-[2px]">
                                                                <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center">
                                                                    <Package size={16} />
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className="absolute bottom-0 inset-x-0 bg-black/80 backdrop-blur-sm p-1.5 text-center truncate text-[9px] font-black text-white">
                                                            {card.card_name}
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </>
                                    )
                                ) : globalItemType === 'package' ? (
                                    Array.from(new Set(assets.cards.map(c => c.package_name || 'Standard Editions'))).map(pkgName => {
                                        const pkgCards = assets.cards.filter(c => (c.package_name || 'Standard Editions') === pkgName);
                                        const isSelected = selectedGlobalItem?.name === pkgName;
                                        return (
                                            <div
                                                key={pkgName as string}
                                                onClick={() => setSelectedGlobalItem({ name: pkgName })}
                                                className={`bg-[#1c1c1e] p-4 rounded-2xl border ${isSelected ? 'border-orange-500 shadow-[0_0_20px_rgba(249,115,22,0.3)] scale-[0.98]' : 'border-white/5 hover:border-white/30 hover:scale-[1.02]'} cursor-pointer transition-all aspect-square flex flex-col items-center justify-center text-center group`}
                                            >
                                                <div className="w-12 h-12 rounded-xl bg-orange-500/10 text-orange-500 flex items-center justify-center mb-3 transition-transform">
                                                    <Package size={24} />
                                                </div>
                                                <div className="font-black text-white text-xs truncate w-full px-2">{pkgName as string}</div>
                                                <div className="text-[10px] text-white/40 mt-1">{pkgCards.length} Cards</div>
                                                {isSelected && (
                                                    <div className="mt-2 bg-orange-500 text-white text-[9px] font-black uppercase px-2 py-0.5 rounded-full">
                                                        Selected
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                ) : (
                                    assets.skins.map(skin => {
                                        const isSelected = selectedGlobalItem?.skin_id === skin.skin_id;
                                        return (
                                            <div
                                                key={skin.skin_id}
                                                onClick={() => setSelectedGlobalItem(skin)}
                                                className={`relative rounded-xl border overflow-hidden cursor-pointer transition-all aspect-square ${isSelected ? 'border-orange-500 shadow-[0_0_20px_rgba(249,115,22,0.3)] scale-[0.98]' : 'border-white/10 hover:border-white/30 hover:scale-[1.02]'}`}
                                            >
                                                {skin.url ? (
                                                    <img src={skin.url} alt={skin.skin_name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full bg-[#1c1c1e] flex flex-col items-center justify-center p-2 text-center">
                                                        <Box size={24} className="text-white/20 mb-2" />
                                                        <span className="text-[9px] text-white/40 uppercase font-black">{skin.skin_name}</span>
                                                    </div>
                                                )}
                                                {isSelected && (
                                                    <div className="absolute inset-0 bg-orange-500/20 flex items-center justify-center backdrop-blur-[2px]">
                                                        <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center">
                                                            <Package size={16} />
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="absolute bottom-0 inset-x-0 bg-black/80 backdrop-blur-sm p-1.5 text-center truncate text-[9px] font-black text-white">
                                                    {skin.skin_name}
                                                </div>
                                            </div>
                                        )
                                    })
                                )}
                            </div>
                        )}
                    </div>

                    <button
                        onClick={handleBroadcast}
                        disabled={!selectedGlobalItem || isSubmitting}
                        className="w-full py-4 rounded-2xl font-black text-sm uppercase tracking-widest text-white transition-all shadow-[0_0_30px_rgba(249,115,22,0.3)] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                        style={{
                            background: selectedGlobalItem ? 'linear-gradient(135deg, #f97316, #ef4444)' : '#1c1c1e'
                        }}
                    >
                        {isSubmitting ? "Broadcasting..." : "Confirm Global Broadcast"}
                    </button>
                </div>
            </div>
        </div>
    );
};
